import { QueryKey } from '@tanstack/react-query';
import { Identifier } from 'react-admin';

import { LiveLogEntry, LiveLogRange, LiveLogsMeta, LiveLogSource, UsageLogEntry } from 'types/LiveLogs';

/** Root of every translation key the tab uses. */
export const LIVE_LOGS_I18N = 'resources.units.live_logs';

/** The explicit label key of a grid column. */
export const liveLogColumnLabel = (name: string) => `${LIVE_LOGS_I18N}.columns.${name}`;

/** In sub-tab order. */
export const LIVE_LOG_SOURCES: readonly LiveLogSource[] = ['nginx', 'usagelog', 'eventlog'];
export const LIVE_LOG_RANGES: readonly LiveLogRange[] = ['15m', '30m', '1h', '3h', '24h', '72h', '7d', '30d'];

export const DEFAULT_LIVE_LOG_SOURCE: LiveLogSource = 'nginx';
export const DEFAULT_LIVE_LOG_RANGE: LiveLogRange = '15m';

export const LIVE_LOGS_AUTO_REFRESH_MS = 30_000;
export const LIVE_LOGS_PER_PAGE = 25;
export const LIVE_LOGS_ROWS_PER_PAGE_OPTIONS = [25, 50, 100];
/** The API caps `meta.total` here, and paging past it is a 422. */
export const LIVE_LOGS_TOTAL_CAP = 10_000;

/** Datagrid cell and header class for the columns hidden below the `sm` breakpoint. */
export const HIDE_BELOW_SM = 'live-logs-hide-below-sm';

/**
 * Dates in the grids: every part 2-digit, with seconds, 24-hour. `hourCycle: 'h23'`, not
 * `hour12: false`, which Chrome renders as 24:00 at midnight.
 */
export const LOG_DATE_TIME_FORMAT: Intl.DateTimeFormatOptions = {
  year: '2-digit',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
};

const CLOCK_TIME_FORMAT: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
};

export const isLiveLogRange = (value: unknown): value is LiveLogRange =>
  LIVE_LOG_RANGES.some((range) => range === value);

export const isLiveLogSource = (value: unknown): value is LiveLogSource =>
  LIVE_LOG_SOURCES.some((source) => source === value);

/** The react-admin resource of one source. It carries the unit id, so no `resources.<name>.*` keys exist for it. */
export const liveLogResource = (unitId: Identifier, source: LiveLogSource) => `unit/${unitId}/live-logs/${source}`;

/** '1h 02m 05s', '2m 05s' or '45s'. */
export const formatDuration = (seconds: number): string => {
  const total = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = total % 60;
  const pad = (value: number) => String(value).padStart(2, '0');

  if (hours > 0) {
    return `${hours}h ${pad(minutes)}m ${pad(rest)}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${pad(rest)}s`;
  }
  return `${rest}s`;
};

/** HH:mm:ss of a timestamp or an ISO string; '-' for null or anything that is not a date. */
export const formatClockTime = (value: number | string | null): string => {
  if (value === null) {
    return '-';
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleTimeString(undefined, CLOCK_TIME_FORMAT);
};

/**
 * The page of a react-admin getList query, read from its key
 * `[resource, 'getList', { pagination, sort, filter, meta }]`.
 */
export const getListQueryPage = (queryKey: QueryKey): number | undefined => {
  const params: unknown = queryKey[2];
  if (typeof params !== 'object' || params === null || !('pagination' in params)) {
    return undefined;
  }

  const { pagination } = params;
  if (typeof pagination !== 'object' || pagination === null || !('page' in pagination)) {
    return undefined;
  }

  return typeof pagination.page === 'number' ? pagination.page : undefined;
};

/** Only an entry with at least one field can be expanded. */
export const hasFields = (record: LiveLogEntry): boolean => Object.keys(record.fields ?? {}).length > 0;

const isUsageLogEntry = (value: unknown): value is UsageLogEntry =>
  typeof value === 'object' && value !== null && 'id' in value && 'is_running' in value;

/**
 * `meta.running` of a usagelog response: the session, null when nothing runs, or undefined while
 * there is no response yet (or the shape is not one we know).
 */
export const readRunningSession = (meta: unknown): LiveLogsMeta['running'] => {
  if (typeof meta !== 'object' || meta === null || !('running' in meta)) {
    return undefined;
  }

  const { running } = meta;
  if (running === null) {
    return null;
  }

  return isUsageLogEntry(running) ? running : undefined;
};
